<template>
  <div class="autocomplete-wrapper">
    <div class="search-input">
      <a class="uk-form-icon uk-form-icon-flip" v-on:click="onSearchClicked" uk-icon="icon: search"></a>
      <input style="display:block"  @click.stop="onFocused" @keydown.enter="onEnter" id="autocomplete" class="uk-input uk-form-small" :placeholder="placeholder" type="text" @change="onChanged" enterkeyhint="search" />
    </div>
  </div>
</template>

<script setup>
import uikit from 'uikit'
import { onMounted, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { geocoding } from '@/utils/geocoding'
import MapService from '@/services/MapService.ts'

const store = useStore()

const props = defineProps({
  /**
   * プレースホルダー
   * 多言語化するときは注意
   */
  placeholder: {
    type: String,
    default: storelocator.messages.map_search_place
  },
  /**
   * デフォルトで入力するクエリ
   */
  query: {
    type: String,
    default: null,
  },
})

onMounted(() => {
  if (props.query) {
    document.getElementById("autocomplete").value = props.query
  }
  if (storelocator.map.searchers?.places?.autocomplete?.enabled) {
    initAutocomplete()
  }
})

function onEnter(e) {
  if (!e.isComposing) {
    document.getElementById("autocomplete").blur()
    onSearchClicked()
  }
}

const messages = computed(() => storelocator.messages)

/**
 * Autocompleteに書き換えられていないクエリをもっておきたい
 */
let inputtedPlaceQuery = ''
function onChanged() {
  inputtedPlaceQuery = document.getElementById('autocomplete').value
}

// イベントが複数発火したときの防止用
let searching = false

function initAutocomplete() {
  const autocomplete = new google.maps.places.Autocomplete(
    document.getElementById('autocomplete'),
    {
      componentRestrictions: {country: 'jp'},
      fields: ['geometry', 'address_components']
    }
  );

  autocomplete.addListener('place_changed', async () => {
    // console.debug('place_changed')

    if (searching) {
      // console.debug('intterupt place_changed')
      return
    }

    searching = true

    const place = autocomplete.getPlace()
    let geometry
    let addressComponents
    let by

    if (place.geometry) {
      geometry = place.geometry
      addressComponents = place.address_components
      by = 'Places'
    } else {
      try {
        const geocoded = await geocoding(place.name)
        geometry = geocoded.geometry
        addressComponents = geocoded.address_components
        by = 'Geocoding'
      } catch (status) {
        console.warn('geocode failed', status)
        if (status === 'ZERO_RESULTS') {
          uikit.notification({
            message: messages.value.map_search_place_zero_result,
            pos: 'top-center',
            timeout: 3000
          })
        } else if (status === 'ERROR') {
          uikit.notification({
            message: messages.value.common_failed_network_connect,
            pos: 'top-center',
            timeout: 3000
          })
        }

        searching = false
        return
      }
    }

    // 送信
    if (storelocator.analytics.client) {
      storelocator.analytics.client.context.PlaceQuery = inputtedPlaceQuery
      storelocator.analytics.client.context.Pref = addressComponents.find((c) => c.types.includes('administrative_area_level_1'))?.long_name
      storelocator.analytics.client.context.City = addressComponents.find((c) => c.types.includes('locality'))?.long_name
      storelocator.analytics.client.send('SearchPlace', {
        'SearchPlaceBy': by,
      })
    }

    const bounds = geometry.viewport
    if (bounds) {
      MapService.fitBounds(bounds, 17)
    } else {
      MapService.map.setZoom(17);
      MapService.map.panTo(place.geometry.location);
    }
    blur()

    store.dispatch("closePanel")

    searching = false
  })
}

/**
 * 検索アイコンクリック時
 * 
 * この時は候補を取れないので、Geocodingに流す
 */
async function onSearchClicked() {
  // console.debug('onSearchClicked')

  if (searching) {
    // console.debug('intterupt onSearchClicked')
    return
  }

  searching = true

  // vue の変数だと Autocomplete が書き換えた値を拾えない
  const query = document.getElementById('autocomplete').value
  if (query) {
    let geocoded = null
    try {
      geocoded = await geocoding(query)
    } catch (status) {
      console.warn('geocode failed', status)
      if (status === 'ZERO_RESULTS') {
        uikit.notification({
          message: messages.value.map_search_place_zero_result,
          pos: 'top-center',
          timeout: 3000
        })
      } else if (status === 'ERROR') {
        uikit.notification({
          message: messages.value.common_failed_network_connect,
          pos: 'top-center',
          timeout: 3000
        })
      }

      searching = false
      return
    }

    const geometry = geocoded.geometry

    // 送信
    if (storelocator.analytics.client) {
      storelocator.analytics.client.context.PlaceQuery = inputtedPlaceQuery
      storelocator.analytics.client.context.Pref = geocoded.address_components.find((c) => c.types.includes('administrative_area_level_1'))?.long_name
      storelocator.analytics.client.context.City = geocoded.address_components.find((c) => c.types.includes('locality'))?.long_name
      storelocator.analytics.client.send('SearchPlace', {
        'SearchPlaceBy': 'Geocoding',
      })
    }

    const bounds = geometry.viewport
    if (bounds) {
      MapService.fitBounds(bounds, 17)
    } else {
      MapService.map.setZoom(17)
      MapService.map.panTo(place.geometry.location)
    }

    store.dispatch("closePanel")

    searching = false
  }

  searching = false
}

async function onFocused() {
  document.getElementById("autocomplete").value = ""
  if (store.getters.isPanelClosed) {
    store.dispatch("openPanel")
  }
}

/**
 * 現在地を取得・移動したら入力を消す
 * 表示位置との差が生まれる可能性があり、混乱するため
 */
watch(() => store.getters.currentLocation, () => {
  document.getElementById("autocomplete").value = ""
})

function clear() {
  document.getElementById("autocomplete").value = ""
}

defineExpose({
  clear,
})
</script>

<style lang="scss">
.autocomplete-wrapper {
  width: 100%;
  position: relative;
  border-radius: 4px;
  transition: all .2s;

  .search-input {
    position: relative;

    #autocomplete {
      width: 100%;
      border: none;
      font-size: 16px;
      border-radius: 20px;
      background-color: #eee;
      text-indent: 0.5rem;
      transition: all .25s;
      height: 40px;
      &::placeholder {
        font-family: system-ui, -apple-system,  sans-serif;
      }
    }
  }

  &:focus-within {
    margin: 0 -2px 0 -2px !important;
    width: calc(100% + 4px);
    #autocomplete {
      background: #777;
      color: #fff;
      transition: all .25s;

      &::placeholder {
        color: #aaa;
      }
    }

    .uk-icon {
      color: #ccc;
      transition: all .25s;
      &:hover {
        color: #fff;
      }
    }
  }
}
</style>
